export function validateNIE(nie: string): boolean {
  const nieRegex = /^[XYZ]\d{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/;
  const nieResidenteRegex = /^[ABCDEFGHJKLMXYZ]\d{7}[TRWAGMYFPDXBNJZSQVHLCKE]$/;

  if (nieRegex.test(nie.toUpperCase()) || nieResidenteRegex.test(nie.toUpperCase())) {
    const letras = 'TRWAGMYFPDXBNJZSQVHLCKE';
    const numeroNIE = parseInt(nie.substr(1, 7), 10);
    const letraCalculada = letras[numeroNIE % 23];
    const letraNIE = nie.charAt(8).toUpperCase();

    return letraCalculada === letraNIE;
  }

  return false;
}

export function validateDNI(dni: string): boolean {
  const dniRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/;

  if (!dniRegex.test(dni.toUpperCase())) {
    return false;
  }

  const letras = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const numeroDNI = parseInt(dni, 10);
  const letraCalculada = letras[numeroDNI % 23];

  const letraDNI = dni.charAt(8).toUpperCase();

  return letraCalculada === letraDNI;
}

export function validateEmail(email: string): boolean {
  const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

  return EMAIL_REGEX.test(email);
}

export const validateIBAN = (iban: string): boolean => {
  if (!iban) {
    return false;
  }

  const formattedIban = iban.replace(/\s+/g, '').toUpperCase();

  if (formattedIban.length < 15 || formattedIban.length > 34) {
    return false;
  }

  const rearrangedIban = formattedIban.slice(4) + formattedIban.slice(0, 4);

  let remainder = rearrangedIban.replace(/[A-Z]/g, (letter) =>
    (letter.charCodeAt(0) - 55).toString()
  );
  while (remainder.length > 2) {
    const chunk = remainder.slice(0, 9);
    remainder = (parseInt(chunk, 10) % 97).toString() + remainder.slice(chunk.length);
  }

  return parseInt(remainder, 10) % 97 === 1;
};

export function cleanFormValues<T extends object>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const [key, value] of Object.entries(obj) as [keyof T, T[keyof T]][]) {
    const isEmpty =
      value === '' ||
      value === null ||
      value === undefined ||
      (typeof value === 'number' && isNaN(value));

    if (!isEmpty) {
      result[key] = value;
    }
  }
  return result;
}

export function formatDecimal(
  input: string | number,
  decimals: number,
  locale: string = 'es-ES'
): string {
  const parsed =
    typeof input === 'number' ? input : Number.parseFloat(input.toString().replace(',', '.'));

  if (isNaN(parsed)) return '';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(parsed);
}

// Format cents input to currency string in euros
// Example: 123456 -> "1.234,56 €"
export function formatCurrency(
  input?: string | number,
  decimals = 2,
  locale: string = 'es-ES'
): string {
  if (input === undefined) {
    return '';
  }
  const parsed =
    typeof input === 'number' ? input : Number.parseFloat(input.toString().replace(',', '.'));

  if (isNaN(parsed)) return '';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    style: 'currency',
    currency: 'EUR',
  }).format(parsed / 100);
}

export function formatPercentage(input?: string | number, locale: string = 'es-ES'): string {
  if (!input) {
    return '-';
  }
  const parsed = typeof input === 'number' ? input : Number.parseFloat(input.toString());

  if (isNaN(parsed)) return '';

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'percent',
  }).format(parsed);
}

export function formatDateToYYYYMMDD(date: Date) {
  return date.toISOString().split('T')[0];
}

// Standard short date for display, e.g. "24 jun 2026".
// Returns `fallback` when the value is missing.
export function formatDate(value?: string | Date | null, fallback = '—'): string {
  if (!value) return fallback;
  return new Date(value).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
